import { isDev } from "../is-dev";

import { LocalizationStore } from "./localization-store";

export type PluralKeys = {
  one: string;
  two: string;
  other: string;
};

export type LocStringCase = "lower" | "upper" | "sentence";

type BaseLocalizationOptions = {
  case?: LocStringCase;
  args?: (string | number)[];
};

export type LocalizationOptions = BaseLocalizationOptions & {
  default?: string;
};

type LocalizationPluralOptions = BaseLocalizationOptions & {
  default?: PluralKeys;
};

export class Localization {
  missingWords: Map<string, string>;

  constructor(private locStore: LocalizationStore) {
    this.locStore = locStore;

    if (isDev) {
      this.missingWords = new Map();
    }
  }

  get lang() {
    return this.locStore.lang;
  }

  get langs() {
    return this.locStore.langs;
  }

  word(key: string, options: LocalizationOptions = {}) {
    return this.getLoc(key, options, () => options.default);
  }

  wordPlural(
    number: number,
    keys: PluralKeys,
    options: LocalizationPluralOptions = {},
  ) {
    if (number === undefined || !keys) {
      // eslint-disable-next-line no-console
      console.warn("[Localization] invalid args", number, keys);

      return "";
    }

    const key = this.getEnding(number, keys);

    return this.getLoc(key, options, () =>
      options.default ? this.getEnding(number, options.default) : undefined,
    );
  }

  hasWord(key: string) {
    const trimmedKey = key.trim();
    const res = this.locStore.words[trimmedKey];

    return res != null && res !== undefined;
  }

  private getLoc(
    key: string,
    options: BaseLocalizationOptions,
    getDefault: () => string | undefined,
  ) {
    const trimmedKey = key.trim();
    let res = this.locStore.words[trimmedKey];
    if (res === null || res === undefined) {
      const def = getDefault();
      if (isDev) {
        if (!trimmedKey.startsWith("Timezone.")) {
          this.missingWords.set(trimmedKey, def ?? "");
          // eslint-disable-next-line no-console
          console.warn(`[Localization]`, trimmedKey, def);
        }
      }

      if (def == null) return `::${key}::`;
      res = def;
    }

    if (options.args != null) res = this.setArgs(res, options.args);
    if (options.case != null) res = this.getCase(res, options.case);

    return res;
  }

  private setArgs(res: string, args: (string | number)[]) {
    let tmp = res;
    for (let index = 0; index < args.length; index++) {
      const el = args[index];
      if (el) {
        tmp = tmp.replace(`{${index}}`, el.toString());
      }
    }

    return tmp;
  }

  private getCase(res: string, stringCase: LocStringCase) {
    switch (stringCase) {
      case "lower":
        return res.toLowerCase();
      case "upper":
        return res.toUpperCase();
      default:
        return res;
    }
  }

  private getEnding(number: number, keys: PluralKeys) {
    const absNumber = Math.abs(number);
    const numArray = [2, 0, 1, 1, 1, 2];
    const index =
      absNumber % 100 <= 4 || absNumber % 100 >= 20
        ? numArray[absNumber % 10 < 5 ? absNumber % 10 : 5]
        : 2;
    switch (index) {
      case 0:
        return keys.one;
      case 1:
        return keys.two;
      case 2:
        return keys.other;
      default: {
        // eslint-disable-next-line no-console
        console.error("Invalid pluralization number");

        return keys.other;
      }
    }
  }
}
